/**
 * Date-range, filtering and formatting helpers for the Cleaner Activity history view.
 * Dates are company-local `YYYY-MM-DD` strings; they are parsed as UTC midnight so the
 * browser timezone never shifts a calendar day.
 */

export const MAX_RANGE_DAYS = 93

export const RANGE_PRESETS = ['this_month', 'last_month', 'this_week', 'last_week', 'custom']
export const DEFAULT_RANGE_PRESET = 'this_month'

export const DEFAULT_HISTORY_SORT = 'name'

const DAY_MS = 24 * 60 * 60 * 1000

function ymdToMs(ymd) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''))
  if (!match) return NaN
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

function msToYmd(ms) {
  return new Date(ms).toISOString().slice(0, 10)
}

export function addDays(ymd, days) {
  return msToYmd(ymdToMs(ymd) + days * DAY_MS)
}

/** Inclusive day count between two `YYYY-MM-DD` strings (0 when invalid or reversed). */
export function rangeDayCount(dateFrom, dateTo) {
  const from = ymdToMs(dateFrom)
  const to = ymdToMs(dateTo)
  if (Number.isNaN(from) || Number.isNaN(to) || from > to) return 0
  return Math.round((to - from) / DAY_MS) + 1
}

/**
 * Range for a preset, relative to the company-local today. Weeks start on Sunday,
 * matching shift working days (1 = Sunday). Current periods end today, not in the future.
 * @param {string} preset
 * @param {string} todayYmd
 * @returns {{ dateFrom: string, dateTo: string } | null} null for 'custom'
 */
export function presetRange(preset, todayYmd) {
  const today = new Date(ymdToMs(todayYmd))
  if (Number.isNaN(today.getTime())) return null
  const year = today.getUTCFullYear()
  const month = today.getUTCMonth()
  const weekday = today.getUTCDay()
  switch (preset) {
    case 'this_month':
      return { dateFrom: msToYmd(Date.UTC(year, month, 1)), dateTo: todayYmd }
    case 'last_month':
      return { dateFrom: msToYmd(Date.UTC(year, month - 1, 1)), dateTo: msToYmd(Date.UTC(year, month, 0)) }
    case 'this_week':
      return { dateFrom: addDays(todayYmd, -weekday), dateTo: todayYmd }
    case 'last_week': {
      const lastWeekEnd = addDays(todayYmd, -weekday - 1)
      return { dateFrom: addDays(lastWeekEnd, -6), dateTo: lastWeekEnd }
    }
    default:
      return null
  }
}

/** Calendar months covered by a range, each with its weeks as 7-slot rows (null = padding). */
export function buildCalendarMonths(dateFrom, dateTo) {
  const fromMs = ymdToMs(dateFrom)
  const toMs = ymdToMs(dateTo)
  if (Number.isNaN(fromMs) || Number.isNaN(toMs) || fromMs > toMs) return []
  const months = []
  const cursor = new Date(fromMs)
  cursor.setUTCDate(1)
  while (cursor.getTime() <= toMs) {
    const year = cursor.getUTCFullYear()
    const month = cursor.getUTCMonth()
    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
    const slots = Array(new Date(Date.UTC(year, month, 1)).getUTCDay()).fill(null)
    for (let day = 1; day <= daysInMonth; day++) slots.push(msToYmd(Date.UTC(year, month, day)))
    while (slots.length % 7) slots.push(null)
    const weeks = []
    for (let i = 0; i < slots.length; i += 7) weeks.push(slots.slice(i, i + 7))
    months.push({ key: `${year}-${month + 1}`, year, month, weeks })
    cursor.setUTCMonth(month + 1)
  }
  return months
}

/**
 * @param {Object} person
 * @param {string} search
 * @param {Set<number>|null} effectiveRoomIds rooms of the selected working areas (null = no filter)
 * @param {number[]} workingAreaIds
 */
export function personMatchesFilter(person, search, effectiveRoomIds, workingAreaIds) {
  const term = String(search || '').trim().toLowerCase()
  if (term) {
    const haystack = `${person.username || ''} ${person.phone || ''}`.toLowerCase()
    if (!haystack.includes(term)) return false
  }
  if (!effectiveRoomIds) return true
  const personAreaIds = (person.workingAreaIds || []).map(Number)
  if (workingAreaIds.some((id) => personAreaIds.includes(Number(id)))) return true
  return (person.roomIds || []).some((roomId) => effectiveRoomIds.has(Number(roomId)))
}

function byName(a, b) {
  return String(a.username || a.phone || '').localeCompare(String(b.username || b.phone || ''))
}

/** Higher first; unknown (null) values always last. */
function byDesc(field) {
  return (a, b) => {
    const va = a.totals[field]
    const vb = b.totals[field]
    if (va == null && vb == null) return byName(a, b)
    if (va == null) return 1
    if (vb == null) return -1
    return vb - va || byName(a, b)
  }
}

/** Late days + missed days + sessions without clock-out (unknown late/missed count as 0). */
export function issueCount(person) {
  const totals = person.totals
  return (totals.lateDays || 0) + (totals.missedDays || 0) + (totals.missingClockOuts || 0)
}

export function sortPeople(people, sortKey = DEFAULT_HISTORY_SORT) {
  const list = [...people]
  switch (sortKey) {
    case 'most_hours':
      return list.sort(byDesc('netMinutes'))
    case 'least_hours':
      return list.sort((a, b) => a.totals.netMinutes - b.totals.netMinutes || byName(a, b))
    case 'most_days':
      return list.sort(byDesc('daysWorked'))
    case 'most_issues':
      return list.sort((a, b) => issueCount(b) - issueCount(a) || byName(a, b))
    default:
      return list.sort(byName)
  }
}

/** Hour totals for the currently listed people (the three History headline cards). */
export function summarizePeople(people) {
  return people.reduce(
    (acc, { totals }) => {
      acc.totalGrossMinutes += totals.grossMinutes
      acc.totalNetMinutes += totals.netMinutes
      acc.totalBreakMinutes += totals.breakMinutes
      return acc
    },
    { totalGrossMinutes: 0, totalNetMinutes: 0, totalBreakMinutes: 0 },
  )
}

/** "Sun, 4 Oct" in the UI locale, for a company-local `YYYY-MM-DD`. */
export function formatDayLabel(ymd, locale, { withYear = false } = {}) {
  const ms = ymdToMs(ymd)
  if (Number.isNaN(ms)) return ''
  const options = { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }
  if (withYear) options.year = 'numeric'
  return new Date(ms).toLocaleDateString(locale || 'en', options)
}

export function formatMonthLabel(year, month, locale) {
  return new Date(Date.UTC(year, month, 1)).toLocaleDateString(locale || 'en', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/** Short weekday names starting Sunday, in the UI locale. */
export function weekdayShortNames(locale) {
  // 2023-01-01 was a Sunday.
  return Array.from({ length: 7 }, (_, index) =>
    new Date(Date.UTC(2023, 0, 1 + index)).toLocaleDateString(locale || 'en', { weekday: 'narrow', timeZone: 'UTC' }),
  )
}
